import { supabase } from '../supabase';
import { Tables, TablesUpdate } from '@/types/database.types';
import { handleError, checkWorkspaceAccess, checkWorkspaceAdmin } from './db-utils';
import { currentType } from '../environment';

// Type aliases for cleaner code
type WorkspaceRow = Tables<'workspaces'>;
type WorkspaceUpdate = TablesUpdate<'workspaces'>;

// Workspace with role info
export type WorkspaceWithRole = WorkspaceRow & {
  role: 'admin' | 'reader' | 'superadmin' | null;
  is_member: boolean;
};

/**
 * Get workspaces for the current user with their role
 */
export const getUserWorkspaces = async (): Promise<WorkspaceWithRole[]> => {
  try {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) throw new Error('User not authenticated');

    const { data, error } = await supabase
      .from('workspaces')
      .select(`
        *,
        workspace_users!inner(user_id, role)
      `)
      .eq('workspace_users.user_id', user.id)
      .eq('type', currentType())
      .order('created_at', { ascending: false });

    if (error) handleError(error, 'fetch user workspaces');

    // Extract the role from the nested workspace_users array
    return (data || []).map(workspace => {
      const workspaceUsers = workspace.workspace_users as unknown as { role: 'admin' | 'reader' }[];
      return {
        ...workspace,
        workspace_users: undefined, // Remove the nested object
        role: workspaceUsers?.[0]?.role || null,
        is_member: true,
      };
    }) as WorkspaceWithRole[];
  } catch (error) {
    handleError(error, 'fetch user workspaces');
    return [];
  }
};

/**
 * Get all workspaces (for superadmins only)
 * Returns workspaces with role info - 'superadmin' for non-member workspaces
 */
export const getAllWorkspaces = async (): Promise<WorkspaceWithRole[]> => {
  try {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) throw new Error('User not authenticated');

    // Get all workspaces (filtered to current env type — superadmins on
    // staging see staging workspaces, on production see production)
    const { data: allWorkspaces, error: workspacesError } = await supabase
      .from('workspaces')
      .select('*')
      .eq('type', currentType())
      .order('created_at', { ascending: false });

    if (workspacesError) handleError(workspacesError, 'fetch all workspaces');

    // Get user's workspace memberships with roles
    const { data: memberships, error: membershipsError } = await supabase
      .from('workspace_users')
      .select('workspace_id, role')
      .eq('user_id', user.id);

    if (membershipsError) handleError(membershipsError, 'fetch user memberships');

    // Create a map of workspace_id -> role
    const membershipMap = new Map(
      memberships?.map(m => [m.workspace_id, m.role]) || []
    );

    // Mark which workspaces the user is a member of with their role
    return (allWorkspaces || []).map(workspace => {
      const isMember = membershipMap.has(workspace.id);
      return {
        ...workspace,
        is_member: isMember,
        role: isMember ? membershipMap.get(workspace.id)! : 'superadmin',
      };
    });
  } catch (error) {
    handleError(error, 'fetch all workspaces');
    return [];
  }
};

/**
 * Create a new workspace with the current user as admin
 */
export const createWorkspace = async (
  name: string,
  subdomain: string,
  description?: string
): Promise<WorkspaceRow> => {
  try {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) throw new Error('User not authenticated');

    // Validate subdomain format
    if (!/^[a-zA-Z0-9][a-zA-Z0-9-]*[a-zA-Z0-9]$/.test(subdomain)) {
      throw new Error('Invalid subdomain format. Use only letters, numbers, and hyphens.');
    }

    // Check if subdomain is reserved
    const reservedSubdomains = ['www', 'app', 'api', 'admin', 'support', 'mail', 'ftp', 'blog', 'docs'];
    if (reservedSubdomains.includes(subdomain)) {
      throw new Error('This subdomain is reserved');
    }

    // Check if subdomain is available using the public function
    const { data: isAvailable, error: checkError } = await supabase
      .rpc('is_subdomain_available', { check_subdomain: subdomain });

    if (checkError) {
      throw new Error('Failed to check subdomain availability');
    }

    if (!isAvailable) {
      throw new Error('This subdomain is already taken');
    }

    // Create the workspace, stamped with the current environment type
    const { data: workspace, error: createError } = await supabase
      .from('workspaces')
      .insert({
        name,
        subdomain,
        description: description || null,
        type: currentType()
      })
      .select()
      .single();

    if (createError) handleError(createError, 'create workspace');
    if (!workspace) throw new Error('Failed to create workspace');

    // Add the current user as admin
    const { error: memberError } = await supabase
      .from('workspace_users')
      .insert({
        workspace_id: workspace.id,
        user_id: user.id,
        role: 'admin'
      });

    if (memberError) {
      // Try to clean up the workspace if adding user fails
      await supabase.from('workspaces').delete().eq('id', workspace.id);
      handleError(memberError, 'add user to workspace');
    }

    // No subscription row is inserted here — Stripe owns the trial entirely.
    // The frontend's WorkspaceSetup flow redirects new admins straight to
    // Stripe Checkout after workspace creation; the row is upserted by the
    // webhook on `checkout.session.completed`. Workspaces without a row are
    // treated as free tier (read-time, in useFeatureGate / store/subscription).

    // Create default user preferences (optional, ignore if fails)
    await supabase
      .from('user_preferences')
      .insert({
        user_id: user.id,
        workspace_id: workspace.id
      })
      .select()
      .single();

    // Log activity (optional, ignore if fails)
    await supabase
      .from('activity_logs')
      .insert({
        workspace_id: workspace.id,
        user_id: user.id,
        action: 'create',
        resource_type: 'workspace',
        resource_id: workspace.id,
        metadata: {
          workspace_name: name,
          subdomain: subdomain
        }
      });

    return workspace;
  } catch (error) {
    handleError(error, 'create workspace');
    throw error;
  }
};

/**
 * Get workspace by ID with user access check
 */
export const getWorkspaceById = async (workspaceId: string): Promise<WorkspaceRow | null> => {
  try {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) throw new Error('User not authenticated');

    // Check if user has access to this workspace
    const hasAccess = await checkWorkspaceAccess(workspaceId);
    if (!hasAccess) {
      throw new Error('Access denied to workspace');
    }

    const { data, error } = await supabase
      .from('workspaces')
      .select('*')
      .eq('id', workspaceId)
      .single();

    if (error) {
      if (error.code === 'PGRST116') return null; // Not found
      handleError(error, 'fetch workspace');
    }

    return data;
  } catch (error) {
    handleError(error, 'fetch workspace');
    return null;
  }
};

/**
 * Get workspace by ID for superadmin access (no membership check)
 * This requires the user to have is_superadmin=true in their app_metadata
 * RLS policies will enforce the superadmin check at the database level
 */
export const getWorkspaceByIdAsSuperadmin = async (workspaceId: string): Promise<WorkspaceRow | null> => {
  try {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) throw new Error('User not authenticated');

    // Verify the user is actually a superadmin
    const isSuperadmin = user.app_metadata?.is_superadmin === true;
    if (!isSuperadmin) {
      throw new Error('Superadmin access required');
    }

    // Superadmins can access any workspace - RLS policies will allow this
    const { data, error } = await supabase
      .from('workspaces')
      .select('*')
      .eq('id', workspaceId)
      .single();

    if (error) {
      if (error.code === 'PGRST116') return null; // Not found
      handleError(error, 'fetch workspace as superadmin');
    }

    return data;
  } catch (error) {
    handleError(error, 'fetch workspace as superadmin');
    return null;
  }
};

/**
 * Update workspace
 */
export const updateWorkspace = async (
  workspaceId: string,
  updates: WorkspaceUpdate
): Promise<WorkspaceRow> => {
  try {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) throw new Error('User not authenticated');

    // Check if user is admin of this workspace
    const isAdmin = await checkWorkspaceAdmin(workspaceId);
    if (!isAdmin) {
      throw new Error('Only workspace admins can update workspace settings');
    }

    const { data, error } = await supabase
      .from('workspaces')
      .update({ ...updates, updated_at: new Date().toISOString() })
      .eq('id', workspaceId)
      .select()
      .single();

    if (error) handleError(error, 'update workspace');
    if (!data) throw new Error('No data returned from update');
    return data;
  } catch (error) {
    handleError(error, 'update workspace');
    throw error;
  }
};

/**
 * Delete a workspace (admin only)
 */
export const deleteWorkspace = async (workspaceId: string): Promise<void> => {
  try {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) throw new Error('User not authenticated');

    // Check if user is admin of this workspace
    const isAdmin = await checkWorkspaceAdmin(workspaceId);
    if (!isAdmin) {
      throw new Error('Only workspace admins can delete workspaces');
    }

    // Delete the workspace - cascade will handle related records
    const { error } = await supabase
      .from('workspaces')
      .delete()
      .eq('id', workspaceId);

    if (error) handleError(error, 'delete workspace');
  } catch (error) {
    handleError(error, 'delete workspace');
    throw error;
  }
};
